import type { ImgHTMLAttributes } from "react";

interface PictureProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** Raster fallback URL (JPEG or PNG). */
  src: string;
  /** Pre-generated WebP URL for the same image. */
  webp: string;
  alt: string;
}

/**
 * <Picture /> — renders a `<picture>` with a WebP source + JPEG/PNG
 * fallback. ~98% of modern browser traffic picks WebP; the fallback
 * covers older iOS, crawlers, and email previewers.
 *
 * Both URLs must be provided explicitly because Vite fingerprints
 * asset imports independently (the WebP file has a different hash).
 * Usage:
 *   import hero from "@/assets/hero.jpg";
 *   import heroWebP from "@/assets/hero.webp";
 *   <Picture src={hero} webp={heroWebP} alt="" />
 */
export function Picture({ src, webp, alt, ...imgProps }: PictureProps) {
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img src={src} alt={alt} {...imgProps} />
    </picture>
  );
}
